import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import 'rxjs/add/operator/delay';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/mergeMap';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { RateLimitUiService } from './rate-limit-ui.service';

interface RateLimitTier {
  limit: number;
  remaining: number;
  windowSeconds: number;
}

@Injectable()
export class RateLimitService {
  private readonly MAX_RETRIES = 3;
  private readonly BASE_DELAY = 1000;
  private readonly DEFAULT_WINDOW = 60; // fallback value in seconds
  private readonly THROTTLE_THRESHOLD = 0.2; // Show UI when 20% capacity left
  
  private retryCount = 0;
  private windowStart = Date.now();
  private requestsInWindow = 0;
  
  // Rate limit tiers
  private minuteLimit: RateLimitTier = { limit: Infinity, remaining: Infinity, windowSeconds: 60 };
  private hourLimit: RateLimitTier = { limit: Infinity, remaining: Infinity, windowSeconds: 3600 };
  private dayLimit: RateLimitTier = { limit: Infinity, remaining: Infinity, windowSeconds: 86400 };
  
  private retryAfterSeconds$ = new BehaviorSubject<number | null>(null);
  private windowSize$ = new BehaviorSubject<number>(this.DEFAULT_WINDOW);

  constructor(private rateLimitUi: RateLimitUiService) {}

  updateRateLimitInfo(headers: any) {
    if (!headers) return;
    
    // Parse multi-tier rate limit headers with safe fallbacks
    const minuteLimit = parseInt(headers.get('X-Rate-Limit-Minute-Limit'));
    const minuteRemaining = parseInt(headers.get('X-Rate-Limit-Minute-Remaining'));
    
    const hourLimit = parseInt(headers.get('X-Rate-Limit-Hour-Limit'));
    const hourRemaining = parseInt(headers.get('X-Rate-Limit-Hour-Remaining'));
    
    const dayLimit = parseInt(headers.get('X-Rate-Limit-Day-Limit'));
    const dayRemaining = parseInt(headers.get('X-Rate-Limit-Day-Remaining'));
    
    // Only update values if they are valid numbers
    if (!isNaN(minuteLimit) && !isNaN(minuteRemaining)) {
      this.minuteLimit = {
        limit: minuteLimit,
        remaining: minuteRemaining,
        windowSeconds: 60
      };
    }
    
    if (!isNaN(hourLimit) && !isNaN(hourRemaining)) {
      this.hourLimit = {
        limit: hourLimit,
        remaining: hourRemaining,
        windowSeconds: 3600
      };
    }
    
    if (!isNaN(dayLimit) && !isNaN(dayRemaining)) {
      this.dayLimit = {
        limit: dayLimit,
        remaining: dayRemaining,
        windowSeconds: 86400
      };
    }
    
    const retryAfterSeconds = parseInt(headers.get('X-Rate-Limit-Retry-After-Seconds')) || 
                              parseInt(headers.get('Retry-After')) || 
                              null;
    
    this.retryAfterSeconds$.next(retryAfterSeconds);
    
    // Reset window if needed
    const now = Date.now();
    if (now > this.windowStart + (this.minuteLimit.windowSeconds * 1000)) {
      this.windowStart = now;
      this.requestsInWindow = 0;
    }
    
    this.requestsInWindow++;

    // Find the most restrictive tier for UI display
    const mostRestrictiveTier = this.getMostRestrictiveTier();
    const isThrottled = this.shouldThrottle();
    
    // Force UI update for debugging - always show status
    console.log('Rate limit status:', {
      tier: mostRestrictiveTier,
      isThrottled,
      minuteLimit: this.minuteLimit,
      hourLimit: this.hourLimit,
      dayLimit: this.dayLimit
    });
    
    // Calculate seconds until reset for UI
    let resetTimeoutSeconds = retryAfterSeconds || 0;
    
    if (!resetTimeoutSeconds) {
      // Default to the window size of the most restrictive tier
      resetTimeoutSeconds = mostRestrictiveTier.windowSeconds;
    }

    // Calculate reset time (now + retryAfter or default to minutes reset)
    const resetTime = retryAfterSeconds ? 
      new Date(Date.now() + (retryAfterSeconds * 1000)) : 
      new Date(Date.now() + (mostRestrictiveTier.windowSeconds * 1000));

    // Update UI state with proper parameters
    this.rateLimitUi.updateStatus(
      mostRestrictiveTier.remaining,
      mostRestrictiveTier.limit,
      resetTime,
      resetTimeoutSeconds,
      isThrottled
    );
  }

  shouldThrottle(): boolean {
    // Check if any tier is active and has a limit
    const minuteActive = this.minuteLimit.limit !== Infinity && this.minuteLimit.limit > 0;
    const hourActive = this.hourLimit.limit !== Infinity && this.hourLimit.limit > 0;
    const dayActive = this.dayLimit.limit !== Infinity && this.dayLimit.limit > 0;
    
    // If we have actual limits, check if any tier is below threshold
    if (minuteActive && this.minuteLimit.remaining / this.minuteLimit.limit < this.THROTTLE_THRESHOLD) {
      return true;
    }
    
    if (hourActive && this.hourLimit.remaining / this.hourLimit.limit < this.THROTTLE_THRESHOLD) {
      return true;
    }
    
    if (dayActive && this.dayLimit.remaining / this.dayLimit.limit < this.THROTTLE_THRESHOLD) {
      return true;
    }
    
    // Also throttle if explicitly told to retry after
    return this.retryAfterSeconds$.value !== null;
  }

  getMostRestrictiveTier(): RateLimitTier {
    // Calculate percentages only for tiers with valid limits
    let minutePercentage = 1.0; // Default to 100%
    let hourPercentage = 1.0;
    let dayPercentage = 1.0;
    
    if (this.minuteLimit.limit !== Infinity && this.minuteLimit.limit > 0) {
      minutePercentage = this.minuteLimit.remaining / this.minuteLimit.limit;
    }
    
    if (this.hourLimit.limit !== Infinity && this.hourLimit.limit > 0) {
      hourPercentage = this.hourLimit.remaining / this.hourLimit.limit;
    }
    
    if (this.dayLimit.limit !== Infinity && this.dayLimit.limit > 0) {
      dayPercentage = this.dayLimit.remaining / this.dayLimit.limit;
    }
    
    // Return the tier with lowest remaining percentage
    if (minutePercentage <= hourPercentage && minutePercentage <= dayPercentage) {
      return this.minuteLimit;
    } else if (hourPercentage <= minutePercentage && hourPercentage <= dayPercentage) {
      return this.hourLimit;
    } else {
      return this.dayLimit;
    }
  }

  getThrottleDelay(): number {
    const retryAfter = this.retryAfterSeconds$.value;
    
    if (retryAfter) {
      return retryAfter * 1000;
    }
    
    // Use exponential backoff with a cap based on the most restrictive tier
    const mostRestrictiveTier = this.getMostRestrictiveTier();
    return Math.min(
      this.BASE_DELAY * Math.pow(2, this.retryCount - 1),
      mostRestrictiveTier.windowSeconds * 1000
    );
  }

  handleRateLimitError(error: HttpErrorResponse): Observable<any> {
    if (error.status !== 429) {
        this.retryCount = 0;
        return Observable.throw(error);
    }

    // Parse retry headers with explicit fallbacks
    const retryAfter = error.headers && (
      parseInt(error.headers.get('X-Rate-Limit-Retry-After-Seconds')) || 
      parseInt(error.headers.get('Retry-After')) || 
      null
    );
    
    // Calculate delay for retry
    let delay: number;
    if (retryAfter) {
      delay = retryAfter * 1000;
    } else {
      delay = Math.min(this.BASE_DELAY * Math.pow(2, this.retryCount - 1), 10000);
    }

    // Ensure delay is not zero to prevent immediate retries
    delay = Math.max(delay, 1000);

    // For 429 errors, update rate limit information from headers
    if (error.headers) {
      this.updateRateLimitInfo(error.headers);
    } else {
      // If no headers, just show a generic rate limit warning with a countdown
      const resetTimeoutSeconds = Math.ceil(delay / 1000);
      this.rateLimitUi.updateStatus(
        0,                                    // remaining
        100,                                  // limit
        new Date(Date.now() + delay),         // resetTime
        resetTimeoutSeconds,                  // resetTimeoutSeconds
        true                                  // isThrottled
      );
    }

    this.retryCount++;
    
    if (this.retryCount > this.MAX_RETRIES) {
        this.retryCount = 0;
        return Observable.throw(error);
    }

    return of(null).delay(delay);
  }
}


// WEBPACK FOOTER //
// ./src/app/core/services/rate-limit.service.ts